import React, { useEffect, useState } from "react";
import { api, unwrapArray } from "@/lib/api";
import { SearchSelect } from "@/components/ui/search-select";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Package, Plus, Edit, Trash2, Loader2, AlertCircle } from "lucide-react";
import { formatINR } from "@/lib/utils";
import type { DealLineItemRecord, ProductVariantRecord } from "@/types";

const GST_RATES = [0, 5, 12, 18, 28];
const CUSTOM_LINE = "__custom__";

const EMPTY_FORM = {
  product_id: "",
  product_name: "",
  unit: "NOS",
  quantity: "1",
  unit_price: "",
  discount_pct: "0",
  gst_rate: 18,
  variant_id: "",
};

const selectClass =
  "h-10 sm:h-11 w-full rounded-lg border border-border bg-background px-3 sm:px-3.5 text-sm sm:text-[15px] text-foreground focus:outline-hidden focus:ring-2 focus:ring-ring";

interface DealProductsCardProps {
  dealId: string;
  isClosed: boolean;
  /**
   * Called with the current line count after the initial load (amountChanged = false)
   * and after every add/edit/remove (amountChanged = true) so the parent can reload the deal.
   */
  onChanged: (lineCount: number, amountChanged: boolean) => void;
}

export function DealProductsCard({ dealId, isClosed, onChanged }: DealProductsCardProps) {
  const [lines, setLines] = useState<DealLineItemRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // null = closed, "new" = add, record = edit
  const [dialogLine, setDialogLine] = useState<DealLineItemRecord | "new" | null>(null);
  const [deleteLine, setDeleteLine] = useState<DealLineItemRecord | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [variants, setVariants] = useState<ProductVariantRecord[]>([]);

  const { success, error } = useToast();
  const { hasPermission, user } = useAuth();
  const userLimit = Number(user?.discount_limit_pct || 0);
  const canEdit = hasPermission("deal.update") && !isClosed;
  const canReadProducts = hasPermission("product.read");

  const loadLines = async () => {
    try {
      const res = await api.get(`/api/DealLineItem/ListByDeal/${dealId}`);
      const list = unwrapArray<DealLineItemRecord>(res);
      setLines(list);
      return list;
    } catch (err: any) {
      error("Fetch Error", err.message || "Failed to load deal products");
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLines().then((list) => list && onChanged(list.length, false));
  }, [dealId]);

  const subtotal = lines.reduce((sum, l) => sum + Number(l.line_total || 0), 0);
  const gstTotal = lines.reduce(
    (sum, l) => sum + (Number(l.line_total || 0) * Number(l.gst_rate || 0)) / 100,
    0
  );

  const previewTotal = (() => {
    const qty = Number(form.quantity) || 0;
    const price = Number(form.unit_price) || 0;
    const disc = Number(form.discount_pct) || 0;
    return qty * price * (1 - disc / 100);
  })();

  // Shades of the product in the dialog; stays empty for custom lines or users without catalog access
  const loadVariants = (productId: string, keepId?: string | null) => {
    setVariants([]);
    if (!productId || productId === CUSTOM_LINE || !canReadProducts) return;
    api
      .get(`/api/Product/Variants/${productId}`)
      .then((res) => setVariants(unwrapArray<ProductVariantRecord>(res).filter((v) => v.status !== false || v.id === keepId)))
      .catch(() => setVariants([]));
  };

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setVariants([]);
    setDialogLine("new");
  };

  const openEdit = (l: DealLineItemRecord) => {
    setForm({
      product_id: l.product_id || CUSTOM_LINE,
      product_name: l.product_name,
      unit: l.unit,
      quantity: String(Number(l.quantity)),
      unit_price: String(Number(l.unit_price)),
      discount_pct: String(Number(l.discount_pct)),
      gst_rate: Number(l.gst_rate),
      variant_id: l.variant_id || "",
    });
    loadVariants(l.product_id || "", l.variant_id);
    setDialogLine(l);
  };

  // A shade with its own price sets the line price; the price can still be typed over
  const handleVariantPick = (variantId: string) => {
    const v = variants.find((x) => x.id === variantId);
    setForm({
      ...form,
      variant_id: variantId,
      ...(v && v.unit_price !== null && v.unit_price !== undefined ? { unit_price: String(Number(v.unit_price)) } : {}),
    });
  };

  // Picking a catalog product pre-fills its price, unit and GST
  const handleProductPick = (productId: string, row?: any) => {
    if (!row || productId === CUSTOM_LINE) {
      setForm({ ...form, product_id: productId, product_name: productId === CUSTOM_LINE ? form.product_name : "", variant_id: "" });
      setVariants([]);
      return;
    }
    setForm({
      ...form,
      product_id: row.id,
      product_name: row.name,
      unit: row.unit,
      unit_price: String(Number(row.unit_price)),
      gst_rate: Number(row.gst_rate),
      variant_id: "",
    });
    loadVariants(row.id);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isCustom = form.product_id === CUSTOM_LINE || !form.product_id;
    if (dialogLine === "new" && !form.product_id) {
      error("Validation Error", "Select a product or choose a custom line");
      return;
    }
    if (isCustom && !form.product_name.trim()) {
      error("Validation Error", "Enter a description for the custom line");
      return;
    }
    if (!(Number(form.quantity) > 0)) {
      error("Validation Error", "Quantity must be greater than zero");
      return;
    }
    const discount = Number(form.discount_pct) || 0;
    if (discount < 0 || discount > 100) {
      error("Validation Error", "Discount must be between 0 and 100");
      return;
    }

    const payload = {
      deal_id: dealId,
      product_id: isCustom ? undefined : form.product_id,
      product_name: form.product_name.trim() || undefined,
      unit: form.unit,
      quantity: Number(form.quantity),
      unit_price: form.unit_price === "" ? undefined : Number(form.unit_price),
      discount_pct: discount,
      gst_rate: Number(form.gst_rate),
      // Custom lines have no shades; clearing sends null so a removed shade is saved as removed
      variant_id: isCustom ? undefined : form.variant_id || null,
    };

    setActionLoading(true);
    try {
      if (dialogLine === "new") {
        await api.post("/api/DealLineItem/Insert", payload);
        success("Product Added", "Deal amount has been recalculated");
      } else if (dialogLine) {
        await api.put(`/api/DealLineItem/Update/${dialogLine.id}`, payload);
        success("Line Updated", "Deal amount has been recalculated");
      }
      setDialogLine(null);
      const list = await loadLines();
      onChanged(list ? list.length : lines.length, true);
    } catch (err: any) {
      error("Save Failed", err.message || "Failed to save product line");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteLine) return;
    setActionLoading(true);
    try {
      await api.delete(`/api/DealLineItem/Delete/${deleteLine.id}`);
      success("Line Removed", "Product removed from deal");
      setDeleteLine(null);
      const list = await loadLines();
      onChanged(list ? list.length : lines.length, true);
    } catch (err: any) {
      error("Delete Failed", err.message || "Failed to remove product line");
    } finally {
      setActionLoading(false);
    }
  };

  const isCustomForm = form.product_id === CUSTOM_LINE;

  return (
    <div className="bg-card border rounded-2xl shadow-xs overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b">
        <div className="flex items-center gap-2">
          <Package className="size-4 text-primary" />
          <h3 className="text-sm font-bold text-foreground">Products</h3>
          {lines.length > 0 && (
            <span className="text-[10px] px-1.5 rounded-full bg-muted text-muted-foreground">
              {lines.length}
            </span>
          )}
        </div>
        {canEdit && (
          <Button size="sm" variant="outline" onClick={openAdd} className="h-8 gap-1.5 text-xs">
            <Plus className="size-3.5" />
            Add Product
          </Button>
        )}
      </div>

      {loading ? (
        <div className="py-8 flex justify-center">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : lines.length === 0 ? (
        <div className="py-6 px-4 text-center text-xs text-muted-foreground">
          No products on this deal yet.
          {canEdit && " Add products to calculate the deal amount automatically."}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Product</TableHead>
                  <TableHead className="text-xs text-right">Qty</TableHead>
                  <TableHead className="text-xs text-right">Price</TableHead>
                  <TableHead className="text-xs text-right">Disc.</TableHead>
                  <TableHead className="text-xs text-right">GST</TableHead>
                  <TableHead className="text-xs text-right">Total</TableHead>
                  {canEdit && <TableHead className="w-16" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell className="text-xs">
                      <div className="font-medium text-foreground">{l.product_name}</div>
                      {l.product_code && (
                        <code className="text-[10px] text-muted-foreground">{l.product_code}</code>
                      )}
                      {l.variant_name && <div className="text-[11px] text-muted-foreground">Shade: {l.variant_name}</div>}
                    </TableCell>
                    <TableCell className="text-xs text-right whitespace-nowrap">
                      {Number(l.quantity)} {l.unit}
                    </TableCell>
                    <TableCell className="text-xs text-right">{formatINR(l.unit_price)}</TableCell>
                    <TableCell className="text-xs text-right">
                      {Number(l.discount_pct) > 0 ? (
                        <div className="flex items-center justify-end gap-1">
                          <span>{Number(l.discount_pct)}%</span>
                          {Number(l.discount_pct) > userLimit && (
                            <span
                              title={`Above your ${userLimit}% limit: will require approval when quoted`}
                              className="inline-flex items-center text-amber-600 dark:text-amber-400"
                            >
                              <AlertCircle className="size-3" />
                            </span>
                          )}
                        </div>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-right">{Number(l.gst_rate)}%</TableCell>
                    <TableCell className="text-xs text-right font-semibold">
                      {formatINR(l.line_total)}
                    </TableCell>
                    {canEdit && (
                      <TableCell className="text-right whitespace-nowrap">
                        <Button variant="ghost" size="icon" className="size-7" onClick={() => openEdit(l)} title="Edit line">
                          <Edit className="size-3.5 text-muted-foreground" />
                        </Button>
                        <Button variant="ghost" size="icon" className="size-7" onClick={() => setDeleteLine(l)} title="Remove line">
                          <Trash2 className="size-3.5 text-red-500" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Totals */}
          <div className="border-t px-4 py-3 text-xs space-y-1 ml-auto max-w-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Subtotal (deal amount)</span>
              <span className="font-semibold">{formatINR(subtotal)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">GST</span>
              <span>{formatINR(gstTotal)}</span>
            </div>
            <div className="flex justify-between border-t pt-1 text-sm">
              <span className="font-semibold">Total incl. GST</span>
              <span className="font-bold">{formatINR(subtotal + gstTotal)}</span>
            </div>
          </div>
        </>
      )}

      {/* Dialog: Add / Edit line */}
      <Dialog open={!!dialogLine} onOpenChange={(open) => !open && setDialogLine(null)}>
        <DialogContent className="sm:max-w-2xl md:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg sm:text-xl font-bold">
              <Package className="size-5 text-primary" />
              {dialogLine === "new" ? "Add Product to Deal" : "Edit Product Line"}
            </DialogTitle>
            <DialogDescription>Prices exclude GST. The deal amount updates automatically.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-4.5 py-2">
            {dialogLine === "new" && (
              <div className="space-y-2">
                <Label>Product *</Label>
                {canReadProducts ? (
                  <SearchSelect
                    endpoint="/api/Product/List"
                    extraParams={{ active: "true" }}
                    value={form.product_id}
                    selectedLabel={form.product_id === CUSTOM_LINE ? form.product_name || "+ Custom line" : form.product_name}
                    getLabel={(row) => `${row.name} (${row.code}) · ${formatINR(row.unit_price)}/${row.unit}`}
                    placeholder="Search products"
                    allowEmpty
                    emptyLabel="-- Select a product --"
                    extraOptions={[{ value: CUSTOM_LINE, label: "+ Custom line (not in catalog)" }]}
                    onChange={(id, row) => handleProductPick(id, row)}
                  />
                ) : (
                  <select value={form.product_id} onChange={(e) => handleProductPick(e.target.value)} className={selectClass}>
                    <option value="">-- Select a product --</option>
                    <option value={CUSTOM_LINE}>+ Custom line (not in catalog)</option>
                  </select>
                )}
                {!canReadProducts && (
                  <p className="text-xs text-muted-foreground">
                    You don't have access to the product catalog; only custom lines are available.
                  </p>
                )}
              </div>
            )}

            {!isCustomForm && form.product_id && (variants.length > 0 || form.variant_id) && (
              <div className="space-y-2">
                <Label>Shade / variant</Label>
                <select value={form.variant_id} onChange={(e) => handleVariantPick(e.target.value)} className={selectClass}>
                  <option value="">-- No specific shade --</option>
                  {variants.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name} ({v.code})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {(isCustomForm || dialogLine !== "new") && (
              <div className="space-y-2">
                <Label>Description {isCustomForm && "*"}</Label>
                <Input
                  value={form.product_name}
                  onChange={(e) => setForm({ ...form, product_name: e.target.value })}
                  placeholder="Installation charges"
                />
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <div className="space-y-2">
                <Label>Quantity *</Label>
                <Input
                  type="number"
                  min={0.01}
                  step="0.01"
                  required
                  value={form.quantity}
                  onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Unit Price (₹) *</Label>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  required
                  value={form.unit_price}
                  onChange={(e) => setForm({ ...form, unit_price: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Unit</Label>
                <Input
                  value={form.unit}
                  onChange={(e) => setForm({ ...form, unit: e.target.value.toUpperCase() })}
                  className="font-mono uppercase"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-2">
                <Label>Discount %</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step="0.01"
                  value={form.discount_pct}
                  onChange={(e) => setForm({ ...form, discount_pct: e.target.value })}
                />
                {Number(form.discount_pct || 0) > userLimit && (
                  <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-500/10 border border-amber-500/20 px-2.5 py-1.5 rounded-lg mt-1">
                    <AlertCircle className="size-4 shrink-0" />
                    <span>Above your {userLimit}% limit: will require approval when quoted</span>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>GST Rate</Label>
                <select
                  value={form.gst_rate}
                  onChange={(e) => setForm({ ...form, gst_rate: Number(e.target.value) })}
                  className={selectClass}
                >
                  {GST_RATES.map((r) => (
                    <option key={r} value={r}>
                      {r}%
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="rounded-lg bg-muted/40 px-4 py-3 text-sm flex justify-between">
              <span className="text-muted-foreground">Line total (before GST)</span>
              <span className="font-semibold text-foreground">{formatINR(previewTotal)}</span>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogLine(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading}>
                {actionLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Delete line */}
      <Dialog open={!!deleteLine} onOpenChange={(open) => !open && setDeleteLine(null)}>
        <DialogContent className="sm:max-w-lg md:max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-bold text-red-600">Remove Product</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Remove <strong className="text-foreground">{deleteLine?.product_name}</strong> from this
              deal? The deal amount will be recalculated.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-3">
            <Button type="button" variant="outline" onClick={() => setDeleteLine(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
